import { InjectionToken, Type } from '@angular/core';
import { FormControlOptions, FormControlState } from '@angular/forms';
import { FieldNodeInputDefinitionList } from './graph/model/graph-builder.types';
import { IDeclarativeFieldComponent } from './model/declarative-field-component';
import { FieldPath } from './model/form-declaration';
import { FieldDefinition } from './model/form.types';

/**
 * Options for creating the state for a control tied to a
 * field control.
 */
export interface FieldControlState<T = unknown> {
  options: FormControlOptions;
  state: FormControlState<T>;
  uiState: {
    dirty?: boolean;
  };
}

export interface FieldTrackingOptions {
  category: string;
  action: string;
  namePrefix: string;
}

/**
 * Interface that a field builder must implement.
 */
export interface DeclarativeFieldBuilder<TField extends FieldDefinition> {
  /**
   * Resolves the component type for a `field`.
   *
   * @param field The field for which the component type should be resolved.
   */
  resolveFieldComponentType<C extends IDeclarativeFieldComponent>(
    field: TField,
  ): Type<C>;
  resolveFieldComponentType(field: TField): Type<IDeclarativeFieldComponent>;

  /**
   * Creates the initial options for a field control.
   *
   * @param field The field for which the control state should be created.
   * @param path Path of the field.
   */
  getInitialFieldControlState<T>(
    field: TField,
    path: FieldPath,
  ): FieldControlState<T>;

  /**
   * Allows to apply additional styles for a field.
   *
   * @param field Field for which additional styles should be applied
   * @param path Path to the field
   */
  getFieldStyles(field: TField, path: FieldPath): string;

  /**
   * If defined, it can be used to
   * Provides graph definitions for each field.
   *
   * Function that is called for each field that is instantiated.
   *
   * @param field
   *
   * @returns
   * Field sources to apply for this field.
   */
  getSharedFieldGraphDefinitions?(
    field: TField,
  ): FieldNodeInputDefinitionList | undefined;

  /**
   * If defined, this can be used to setup Analytics-Tracking
   * for field value changes.
   *
   * @param field
   */
  trackFieldValueChanges?(field: TField): FieldTrackingOptions | undefined;
}

/**
 * This token is used to inject the field builder.
 * instantiated.
 */
export const DECLARATIVE_FORMS_FIELD_BUILDER_TOKEN = new InjectionToken<
  DeclarativeFieldBuilder<FieldDefinition>
>('DECLARATIVE_FORMS.FIELD_BUILDER');
